import { useAppStorage } from '@aesop-fables/scrinium';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import { ReportRegistry, mapReportRestToData, reportsRepositoryKey } from '../data/report';
import { toDateKey } from '../helpers/utilityFunctions';
import { ReportRest, BreakdownReport, SummedReport } from '../models/ReportData';
import { AdminTierTypeEnum } from '../services/drillDown';

export const useReportData = () => {
  const appStorage = useAppStorage();
  const reportRepository = appStorage.repository<ReportRegistry>(reportsRepositoryKey);

  const getBreakdownReport = useCallback(
    async (date: Dayjs, tier: AdminTierTypeEnum | undefined): Promise<BreakdownReport | null> => {
      if (!date || !tier) return null;

      const compartmentKey: keyof ReportRegistry =
        tier === AdminTierTypeEnum.Root
          ? 'orgReport'
          : tier === AdminTierTypeEnum.Organization
            ? 'teamReport'
            : 'groupReport';

      const dateKey = toDateKey(date);
      const compartment = reportRepository.get(compartmentKey, dateKey);
      let fetchedReport = await compartment.getData();

      if (!fetchedReport) {
        await compartment.reload();
        fetchedReport = await compartment.getData();
      }

      return fetchedReport
        ? (mapReportRestToData(fetchedReport as ReportRest) as BreakdownReport)
        : null;
    },
    [reportRepository],
  );

  const getSummedReport = useCallback(
    async (
      date: Dayjs,
      tier: AdminTierTypeEnum.Organization | AdminTierTypeEnum.Team,
    ): Promise<SummedReport | null> => {
      if (!date) return null;

      const compartmentKey: keyof ReportRegistry =
        tier === AdminTierTypeEnum.Organization ? 'summedTeamReport' : 'summedGroupReport';

      const dateKey = toDateKey(date);
      const compartment = reportRepository.get(compartmentKey, dateKey);
      let fetchedReport = await compartment.getData();

      if (!fetchedReport) {
        await compartment.reload();
        fetchedReport = await compartment.getData();
      }

      return fetchedReport
        ? (mapReportRestToData(fetchedReport as ReportRest) as SummedReport)
        : null;
    },

    [reportRepository],
  );

  const getEarliestReportDate = useCallback(async (): Promise<Dayjs | null> => {
    const compartment = reportRepository.get('earliestDate', 'earliest');
    let earliestDate = await compartment.getData();

    if (!earliestDate) {
      await compartment.reload();
      earliestDate = await compartment.getData();
    }

    return earliestDate ? dayjs(earliestDate as string) : null;
  }, [reportRepository]);

  return {
    getBreakdownReport,
    getSummedReport,
    getEarliestReportDate,
  };
};
